import React, { createContext, useCallback, useContext, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { CognitoUser, AuthenticationDetails } from 'amazon-cognito-identity-js';

import userPool from '../auth/UserPool';
import { userApi, isSuccessResponse } from '../api/user';
import { API_ENDPOINTS } from '../services/api/endpoints';

interface StatusState {
  Is_Super_Admin: boolean;
  User_Entitlements: any[];
  Is_Loading: boolean;
  Auth_Status: boolean;
  Email_Address: string | null;
  Company_ID: string | null;
  Client_ID: string | null;
  Error_Message: string | null;
  Has_Client_Context: boolean;
  Needs_Onboarding: boolean;
}

interface StatusContextValue extends StatusState {
  authenticate: (email: string, password: string) => Promise<void>;
  logout: () => void;
  Check_User_Status: (email: string) => Promise<Partial<StatusState>>;
}

const StatusContext = createContext<StatusContextValue | null>(null);

export const useStatus = () => {
  const context = useContext(StatusContext);
  if (!context) {
    throw new Error('useStatus must be used within a StatusProvider');
  }
  return context;
};

export const StatusProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const navigate = useNavigate();

  // Initialize state from localStorage to handle React Strict Mode remounts
  const getInitialState = (): StatusState => {
    try {
      const storedUser = localStorage.getItem('user');
      const accessToken = localStorage.getItem('accessToken');

      if (storedUser && accessToken) {
        const user = JSON.parse(storedUser);
        console.log('[StatusProvider] Restoring state from localStorage:', user);
        return {
          Is_Super_Admin: Boolean(user.super_admin),
          User_Entitlements: [],
          Is_Loading: false,
          Auth_Status: true,  // User was authenticated
          Company_ID: user.Company_ID || null,
          Error_Message: null,
          Email_Address: user.email || user.Email_Address || null,
          Client_ID: user.Client_ID || null,
          Has_Client_Context: Boolean(user.Client_ID && user.Company_ID),
          Needs_Onboarding: Boolean(user.needs_onboarding)
        };
      }
    } catch (error) {
      console.error('[StatusProvider] Error restoring state:', error);
    }

    return {
      Is_Super_Admin: false,
      User_Entitlements: [],
      Is_Loading: false,
      Auth_Status: false,
      Company_ID: null,
      Error_Message: null,
      Email_Address: null,
      Client_ID: null,
      Has_Client_Context: false,
      Needs_Onboarding: false
    };
  };

  const [state, setState] = useState<StatusState>(getInitialState);

  // Fetch user data and entitlements from backend
  // Uses /api/auth/authMe endpoint which gets user from Cognito JWT claims
  // Returns state updates to be applied by caller
  const Check_User_Status = useCallback(async (email: string): Promise<Partial<StatusState>> => {
    console.log('[StatusProvider] Fetching user data via /api/auth/authMe');

    // Get access token from localStorage (set during Cognito auth)
    // Check both 'accessToken' (current) and 'idToken' (legacy) for backwards compatibility
    const accessToken = localStorage.getItem('accessToken') || localStorage.getItem('idToken');
    if (!accessToken) {
      throw new Error('No access token available');
    }

    // Call /api/auth/authMe which uses Cognito JWT claims to identify user
    const response = await fetch(API_ENDPOINTS.AUTH.ME, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${accessToken}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({})  // Empty body for POST request
    });

    const userResponse = await response.json();
    console.log('[StatusProvider] Raw userResponse:', JSON.stringify(userResponse, null, 2));

    if (!userResponse.success) {
      throw new Error(userResponse.message || 'Failed to get user details');
    }

    const user = userResponse.data.Records[0];
    console.log('[StatusProvider] User object:', JSON.stringify(user, null, 2));
    console.log('[StatusProvider] user.Super_Admin =', user.Super_Admin, 'type:', typeof user.Super_Admin);

    const Is_Super = Boolean(user.Super_Admin);
    console.log('[StatusProvider] Is_Super after Boolean():', Is_Super);

    // Super admins don't need entitlements - they have global access
    if (Is_Super) {
      console.log('[StatusProvider] Super admin detected - skipping entitlements');

      // Store user data to localStorage for persistence and testing
      const userData = {
        super_admin: true,
        email: user.Email || user.Email_Address,
        ...user
      };
      localStorage.setItem('user', JSON.stringify(userData));

      return {
        Is_Super_Admin: true,
        User_Entitlements: [],
        Client_ID: null,
        Company_ID: null,
        Has_Client_Context: false
      };
    }

    // Get entitlements for regular users
    const entitlementsResponse = await userApi.Get_User_Entitlements({ Email_Address: email });

    // Handle case where user has no entitlements (needs onboarding)
    const entitlements = isSuccessResponse(entitlementsResponse)
      ? entitlementsResponse.data.Records
      : [];
    const firstEntitlement = entitlements[0];
    const needsOnboarding = entitlements.length === 0;

    if (needsOnboarding) {
      console.log('[StatusProvider] User has no entitlements - needs onboarding');
    }

    // Store user data to localStorage for regular users
    const userData = {
      super_admin: false,
      email: user.Email || user.Email_Address,
      needs_onboarding: needsOnboarding,
      ...user
    };
    localStorage.setItem('user', JSON.stringify(userData));

    return {
      Is_Super_Admin: false,
      User_Entitlements: entitlements,
      Client_ID: firstEntitlement?.Client_ID || null,
      Company_ID: firstEntitlement?.Company_ID || null,
      Has_Client_Context: Boolean(firstEntitlement?.Client_ID && firstEntitlement?.Company_ID),
      Needs_Onboarding: needsOnboarding
    };
  }, []);

  // CRITICAL: This function does THREE things per Tim-Combo pattern:
  // 1. Authenticates with Cognito
  // 2. Fetches user data and entitlements from API
  // 3. Updates state which triggers re-render and routing
  const authenticate = useCallback(async (email: string, password: string) => {
    if (!userPool) {
      setState(prev => ({ ...prev, Error_Message: 'Cognito not configured' }));
      return;
    }

    setState(prev => ({ ...prev, Is_Loading: true, Error_Message: null }));

    try {
      const authenticationData = { Username: email, Password: password };
      const authenticationDetails = new AuthenticationDetails(authenticationData);

      const userData = { Username: email, Pool: userPool };
      const cognitoUser = new CognitoUser(userData);

      // Step 1: Authenticate with Cognito (MANDATORY - no custom endpoints)
      await new Promise<void>((resolve, reject) => {
        cognitoUser.authenticateUser(authenticationDetails, {
          onSuccess: async (result) => {
            const idToken = result.getIdToken().getJwtToken();
            const accessToken = result.getAccessToken().getJwtToken();

            // Store tokens - API Gateway Cognito authorizer expects ID token (contains user claims)
            // The 'accessToken' key is what apiClient uses for Authorization header
            localStorage.setItem('accessToken', idToken);  // ID token for API Gateway auth
            localStorage.setItem('idToken', idToken);      // Keep for backwards compatibility
            localStorage.setItem('cognitoAccessToken', accessToken);  // Access token if needed later

            console.log('[StatusProvider] Cognito authentication successful');
            resolve();
          },
          onFailure: (err) => {
            console.error('[StatusProvider] Cognito authentication failed:', err);
            reject(err);
          }
        });
      });

      // Step 2: Fetch user data and entitlements from backend
      const userStatusUpdates = await Check_User_Status(email);

      // Step 3: Update auth status AND user data (triggers routing in SignIn component)
      // Apply all updates in a single setState to avoid race conditions
      console.log('[StatusProvider] Setting final state with:', userStatusUpdates);
      setState(prev => {
        const newState = {
          ...prev,
          ...userStatusUpdates,  // Apply user data updates first
          Auth_Status: true,     // Then set Auth_Status to trigger redirect
          Is_Loading: false,
          Email_Address: email
        };
        console.log('[StatusProvider] New state will be:', { Auth_Status: newState.Auth_Status, Is_Super_Admin: newState.Is_Super_Admin });
        return newState;
      });

    } catch (error: any) {
      console.error('[StatusProvider] Authentication failed:', error);
      setState(prev => ({
        ...prev,
        Is_Loading: false,
        Auth_Status: false,
        Error_Message: error.message || 'Authentication failed'
      }));
      throw error;
    }
  }, [Check_User_Status]);

  const logout = useCallback(() => {
    localStorage.removeItem('idToken');
    localStorage.removeItem('accessToken');
    localStorage.removeItem('user');
    setState({
      Is_Super_Admin: false,
      User_Entitlements: [],
      Is_Loading: false,
      Auth_Status: false,
      Company_ID: null,
      Error_Message: null,
      Email_Address: null,
      Client_ID: null,
      Has_Client_Context: false,
      Needs_Onboarding: false
    });
    navigate('/login');
  }, [navigate]);

  const value: StatusContextValue = {
    ...state,
    authenticate,
    logout,
    Check_User_Status
  };

  return <StatusContext.Provider value={value}>{children}</StatusContext.Provider>;
};
